import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { CheckCircle, Clock, AlertTriangle, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

const QualityApprovedList: React.FC = () => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleItemExpansion = (procurementId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(procurementId)) {
        newSet.delete(procurementId);
      } else {
        newSet.add(procurementId);
      }
      return newSet;
    });
  };

  const { data: procurements, isLoading } = useQuery({
    queryKey: ['quality-approved-procurements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('procurements')
        .select(`
          *,
          procurement_quality!inner(*),
          items:procurement_items(
            id,
            product_name,
            quantity,
            quality_grade,
            item_quality:procurement_item_quality(
              quality_grade,
              damage_kg,
              compensation_kg,
              preliminary_damage_kg,
              preliminary_compensation_kg
            )
          )
        `)
        .not('procurement_quality.quality_approved_by', 'is', null)
        .neq('status', 'rejected')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  if (!procurements || procurements.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <CheckCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No quality-approved procurements yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {procurements.map((procurement) => {
        const quality = (procurement as any).procurement_quality?.[0];
        const items = (procurement as any).items || [];
        const isMultiItem = items.length > 1;
        const isPreliminary = quality?.grading_type === 'preliminary';
        const isFinalComplete = quality?.is_final_grading_complete;
        const hasBothGrades = isPreliminary && isFinalComplete;
        
        // Calculate difference if both exist
        const prelimComp = quality?.preliminary_compensation_kg || 0;
        const finalComp = quality?.compensation_kg || 0;
        const compensationDiff = hasBothGrades ? finalComp - prelimComp : 0;

        // Summarize grades for multi-item procurements
        const gradeSummary = isMultiItem ? items.reduce((acc: Record<string, number>, item: any) => {
          const grade = item.item_quality?.[0]?.quality_grade || item.quality_grade || 'Ungraded';
          acc[grade] = (acc[grade] || 0) + 1;
          return acc;
        }, {} as Record<string, number>) : null;

        return (
          <Card key={procurement.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg">
                    {procurement.product_name || 'Unknown Product'}
                  </CardTitle>
                  {isMultiItem && (
                    <Badge variant="outline" className="text-xs">
                      <Package className="h-3 w-3 mr-1" />
                      {items.length} items
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Badge variant="success">Quality Approved</Badge>
                  {isPreliminary ? (
                    isFinalComplete ? (
                      <Badge variant="default" className="bg-green-600">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Final Complete
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                        <Clock className="h-3 w-3 mr-1" />
                        Preliminary
                      </Badge>
                    )
                  ) : (
                    <Badge variant="default" className="bg-green-600">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Final
                    </Badge>
                  )}
                  <Badge variant="outline">{procurement.status?.replace('_', ' ')}</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Quantity</p>
                  <p className="font-medium">{procurement.total_quantity || 0} kg</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Quality Grade</p>
                  <p className="font-medium">{quality?.quality_grade || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {isPreliminary && !isFinalComplete ? 'Est. Compensation' : 'Compensation'}
                  </p>
                  <p className="font-medium">
                    {isPreliminary && !isFinalComplete 
                      ? `${quality?.preliminary_compensation_kg || 0} kg`
                      : `${quality?.compensation_kg || 0} kg`
                    }
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Damage %</p>
                  <p className="font-medium">{quality?.damage_percentage || 0}%</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Approved On</p>
                  <p className="font-medium">
                    {quality?.quality_approval_date 
                      ? format(new Date(quality.quality_approval_date), 'MMM dd, yyyy')
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Show comparison when both preliminary and final exist */}
              {hasBothGrades && compensationDiff !== 0 && (
                <div className={`mt-4 p-3 rounded-md ${compensationDiff > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                  <div className={`flex items-center gap-2 text-sm ${compensationDiff > 0 ? 'text-red-700' : 'text-green-700'}`}>
                    <AlertTriangle className="h-4 w-4" />
                    <span className="font-medium">
                      {compensationDiff > 0 
                        ? `Final damage was ${Math.abs(compensationDiff)} kg MORE than estimated`
                        : `Final damage was ${Math.abs(compensationDiff)} kg LESS than estimated`
                      }
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Preliminary:</span> {prelimComp} kg
                    </div>
                    <div>
                      <span className="text-muted-foreground">Final:</span> {finalComp} kg
                    </div>
                    <div>
                      <span className="text-muted-foreground">Difference:</span> {compensationDiff > 0 ? '+' : ''}{compensationDiff} kg
                    </div>
                  </div>
                </div>
              )}

              {/* Pending final grading indicator */}
              {isPreliminary && !isFinalComplete && quality?.final_grading_expected_date && (
                <div className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-700">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Final grading expected by: {format(new Date(quality.final_grading_expected_date), 'MMM dd, yyyy')}
                  </p>
                </div>
              )}

              {/* Item-level breakdown for multi-product procurements */}
              {isMultiItem && (
                <Collapsible 
                  open={expandedItems.has(procurement.id)} 
                  onOpenChange={() => toggleItemExpansion(procurement.id)}
                  className="mt-4"
                >
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full justify-between">
                      <span className="flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        View {items.length} Products
                        {gradeSummary && (
                          <span className="text-muted-foreground text-xs">
                            ({Object.entries(gradeSummary).map(([grade, count]) => `${count}x${grade}`).join(', ')})
                          </span>
                        )}
                      </span>
                      {expandedItems.has(procurement.id) ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <div className="bg-muted/50 rounded-md p-3 space-y-2">
                      {items.map((item: any) => {
                        const itemQuality = item.item_quality?.[0];
                        const grade = itemQuality?.quality_grade || item.quality_grade || 'Ungraded';
                        const compensation = itemQuality?.compensation_kg || itemQuality?.preliminary_compensation_kg || 0;
                        const damage = itemQuality?.damage_kg || itemQuality?.preliminary_damage_kg || 0;
                        
                        return (
                          <div key={item.id} className="flex justify-between items-center text-sm border-b border-border/50 pb-2 last:border-0 last:pb-0">
                            <div className="flex-1">
                              <span className="font-medium">{item.product_name}</span>
                              <span className="text-muted-foreground ml-2">({item.quantity} kg)</span>
                            </div>
                            <div className="flex gap-3 items-center">
                              <Badge variant="outline" className="text-xs">
                                Grade: {grade}
                              </Badge>
                              {damage > 0 && (
                                <span className="text-xs text-red-600">
                                  Dmg: {damage} kg
                                </span>
                              )}
                              {compensation > 0 && (
                                <span className="text-xs text-amber-600">
                                  Comp: {compensation} kg
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default QualityApprovedList;
